/*
 * Bundle discount sync (doc/cart-process-analysis.md, 5.2 / section 11).
 *
 * The cart content is the single source of truth. The bundle add-to-cart marks every
 * line of a bundle with hidden line-item properties:
 *   _bundle_id    unique per bundle add (groups the lines)
 *   _bundle_code  discount code of the chosen preset
 *   _bundle_pct   bonus percentage (display only)
 *   _bundle_size  number of distinct lines the bundle consists of
 *   _bundle_qty   minimum quantity of this line (e.g. 2 when a preset holds the same chair twice)
 * A bundle is complete while all of its lines are in the cart, each with at least _bundle_qty
 * (higher quantities are fine).
 *
 * After every cart change, sync() applies the codes of complete bundles to the cart via
 * /cart/update.js {discount} and removes bundle codes whose bundle is no longer complete.
 * Codes the customer entered themselves are kept. Shopify then calculates the discount,
 * so drawer, cart page and checkout all show the same amount and no checkout URL has
 * to be patched. The cart attribute `bundle_discount_code` remembers which codes were
 * applied by the bundle, so they can be removed even after all bundle lines are gone.
 */
(function () {
  var ATTR = 'bundle_discount_code';
  var running = null;
  var queued = false;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function upper(list) {
    return list.map(function (c) { return String(c).trim().toUpperCase(); }).filter(Boolean);
  }

  // Codes of complete bundles (highest percentage first; one code per complete bundle).
  function completeCodes(cart) {
    var groups = {};
    cart.items.forEach(function (item) {
      var p = item.properties || {};
      if (!p._bundle_id || !p._bundle_code) return;
      var g = groups[p._bundle_id] || (groups[p._bundle_id] = {
        code: p._bundle_code, pct: parseFloat(p._bundle_pct) || 0, size: parseInt(p._bundle_size, 10) || 0, lines: 0, short: false
      });
      g.lines += 1;
      if (item.quantity < (parseInt(p._bundle_qty, 10) || 1)) g.short = true;
    });
    var complete = Object.keys(groups).map(function (k) { return groups[k]; })
      .filter(function (g) { return g.size > 0 && g.lines >= g.size && !g.short; })
      .sort(function (a, b) { return b.pct - a.pct; });
    var seen = {};
    return complete.map(function (g) { return g.code; })
      .filter(function (c) { var k = c.toUpperCase(); return seen[k] ? false : (seen[k] = true); });
  }

  // Every code that belongs to the bundle logic: remembered in the attribute or found on a line.
  function bundleCodes(cart) {
    var fromAttr = String((cart.attributes && cart.attributes[ATTR]) || '').split(',');
    var fromLines = cart.items.map(function (i) { return (i.properties || {})._bundle_code || ''; });
    return upper(fromAttr.concat(fromLines));
  }

  function plan(cart) {
    var known = bundleCodes(cart);
    var current = (cart.discount_codes || []).map(function (d) { return d.code; });
    var complete = completeCodes(cart);
    var keep = current.filter(function (c) { return known.indexOf(String(c).toUpperCase()) === -1; });
    var wanted = keep.concat(complete);
    var changed = upper(wanted).sort().join(',') !== upper(current).sort().join(',');
    var registry = complete.join(',');
    var attrChanged = String((cart.attributes && cart.attributes[ATTR]) || '') !== registry ||
      !!(cart.attributes && cart.attributes.bundle_discount_cleared); // legacy flag, cleared once
    return { changed: changed, attrChanged: attrChanged, discount: wanted.join(','), registry: registry };
  }

  // Sections to re-render on the current page (max. 5 per request).
  function sectionSpecs() {
    var specs = [];
    var drawer = document.querySelector('ajax-cart');
    if (drawer) specs.push({ section: 'ajax-cart', target: '#ajaxcart .drawer-inner', selector: '.drawer-inner' });
    var page = document.querySelector('ajax-items');
    var pageSection = document.getElementById('cart-section');
    var totalSection = document.getElementById('cart-section-total');
    if (page && pageSection && pageSection.dataset.id) {
      specs.push({ section: pageSection.dataset.id, target: '#cart-section .js-contents', selector: '.js-contents' });
    }
    if (page && totalSection && totalSection.dataset.id) {
      specs.push({ section: totalSection.dataset.id, target: '#cart-section-total .js-contents-subtotal', selector: '.js-contents-subtotal' });
    }
    if (document.getElementById('ajax-cart-icon')) {
      specs.push({ section: 'ajax-cart-icon', target: '#ajax-cart-icon', selector: '.shopify-section' });
    }
    return specs;
  }

  function render(sections, specs) {
    if (!sections) return;
    specs.forEach(function (s) {
      var html = sections[s.section];
      var target = document.querySelector(s.target);
      if (!html || !target) return;
      var source = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  function post(body) {
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  /**
   * Brings the applied bundle codes in line with the cart content.
   * options.render: re-render drawer / cart page even if nothing changed (after a bundle add)
   * Resolves with the cart state that includes `sections` when something was rendered.
   */
  function sync(options) {
    options = options || {};
    if (running) { queued = true; return running; }
    running = fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var p = plan(cart);
        var specs = sectionSpecs();
        var sectionIds = specs.map(function (s) { return s.section; });
        if (p.changed || p.attrChanged) {
          var body = { attributes: {} };
          body.attributes[ATTR] = p.registry;
          if (cart.attributes && cart.attributes.bundle_discount_cleared) body.attributes.bundle_discount_cleared = '';
          if (p.changed) body.discount = p.discount;
          if (p.changed || options.render) {
            body.sections = sectionIds;
            body.sections_url = window.location.pathname;
          }
          return post(body).then(function (state) {
            render(state.sections, specs);
            return state;
          });
        }
        if (!options.render) return cart;
        return fetch(root() + '?sections=' + encodeURIComponent(sectionIds.join(',')))
          .then(function (r) { return r.json(); })
          .then(function (sections) {
            render(sections, specs);
            cart.sections = sections;
            return cart;
          });
      })
      .then(function (state) {
        document.dispatchEvent(new CustomEvent('bundle:synced', { detail: { cart: state } }));
        return state;
      })
      .catch(function (e) { console.error('Bundle sync failed', e); })
      .finally(function () {
        running = null;
        if (queued) { queued = false; sync(); }
      });
    return running;
  }

  window.DKBundle = { sync: sync, completeCodes: completeCodes };

  // After every cart change (drawer, cart page, product form), but only when the freshly
  // rendered drawer / cart page flags bundle data ([data-dk-bundle], set by Liquid when a
  // line carries _bundle_id or a bundle code is registered). Other carts cost no request.
  // The bundle add calls sync() itself with render: true.
  document.addEventListener('DOMContentLoaded', function () {
    if (typeof user === 'function' && typeof SP_OBJECT !== 'undefined') {
      user(SP_OBJECT.ajxcartRefresh, function () {
        if (document.querySelector('[data-dk-bundle]')) sync();
      });
    }
    // Cart changed elsewhere (other tab, returning visitor): Liquid flags carts with bundle data.
    if (window.dkBundleCheckOnLoad) sync();
  });
})();
